import { openDB } from "idb";

const DB_NAME = "IndoorImagesDB";
const STORE_NAME = "images";
const STORAGE_KEY = "IndoorImagesStore";

// Verifica suporte ao IndexedDB
const isIDBAvailable = () =>
  typeof window !== "undefined" && "indexedDB" in window;

// Utilitários: Blob ↔ Base64
const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const base64ToBlob = (base64: string): Blob => {
  const parts = base64.split(",");
  const mime = parts[0].match(/:(.*?);/)?.[1] || "";
  const binary = atob(parts[1]);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
  return new Blob([array], { type: mime });
};

// IndexedDB
const getDb = async () => {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    },
  });
};

// localStorage helpers
const getLSData = (): any[] => {
  const data = localStorage.getItem(STORAGE_KEY);
  return data ? JSON.parse(data) : [];
};

const saveLSData = (data: any[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

// Ordenação padrão pelo campo "orderImage"
// - Valores numéricos (string ou number) vêm primeiro, em orderImage crescente
// - Itens com ordem null/undefined ou não numérica vão para o final, mantendo ordem relativa
export const sortByOrdem = (data: any[]) =>
  [...data].sort((a, b) => {
    const aRaw = a?.orderImage;
    const bRaw = b?.orderImage;

    const aHasOrder = aRaw !== null && aRaw !== undefined;
    const bHasOrder = bRaw !== null && bRaw !== undefined;

    // Nenhum dos dois tem orderImage -> mantém
    if (!aHasOrder && !bHasOrder) return 0;
    // Só A não tem ordeorderImagem -> vai depois
    if (!aHasOrder) return 1;
    // Só B não tem orderImage -> A vem antes
    if (!bHasOrder) return -1;

    const aOrder = Number(aRaw);
    const bOrder = Number(bRaw);

    const aIsValid = Number.isFinite(aOrder);
    const bIsValid = Number.isFinite(bOrder);

    if (!aIsValid && !bIsValid) return 0;
    if (!aIsValid) return 1;
    if (!bIsValid) return -1;

    return aOrder - bOrder;
  });

// Salvar imagens
export const saveImagesToDB = async (images: any[]) => {
  if (isIDBAvailable()) {
    const db = await getDb();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    for (const image of images) await store.put(image);
    await tx.done;
  } else {
    const current = getLSData();
    const updated = [...current];

    for (const image of images) {
      const base64Blob = await blobToBase64(image.blob);
      const data = { ...image, blob: base64Blob };

      const index = updated.findIndex((item) => item.id === image.id);
      if (index !== -1) updated[index] = data;
      else updated.push(data);
    }

    saveLSData(updated);
  }
};

// Buscar todas (ordenadas por "orderImage")
export const getImagesFromDB = async () => {
  if (isIDBAvailable()) {
    const db = await getDb();
    const all = await db.getAll(STORE_NAME);
    return sortByOrdem(all);
  } else {
    const raw = getLSData();
    const withBlob = raw.map((item) => ({
      ...item,
      blob: base64ToBlob(item.blob),
    }));
    return sortByOrdem(withBlob);
  }
};

// Buscar por ID
export const getImagesFromDBById = async (id: string) => {
  if (isIDBAvailable()) {
    const db = await getDb();
    return await db.get(STORE_NAME, id);
  } else {
    const item = getLSData().find((item) => item.id === id);
    if (!item) return null;
    return { ...item, blob: base64ToBlob(item.blob) };
  }
};

// Atualizar
export const updateImageInDB = async (
  id: string,
  updatedData: Partial<any>
) => {
  if (isIDBAvailable()) {
    const db = await getDb();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const existing = await store.get(id);
    if (!existing) return null;

    const updated = { ...existing, ...updatedData };
    await store.put(updated);
    await tx.done;
    return updated;
  } else {
    const current = getLSData();
    const index = current.findIndex((item) => item.id === id);
    if (index === -1) return null;

    const merged = { ...current[index], ...updatedData };
    current[index] = merged;
    saveLSData(current);
    return { ...merged, blob: base64ToBlob(merged.blob) };
  }
};

// Remover por ID
export const removeImageFromDB = async (id: string) => {
  if (isIDBAvailable()) {
    const db = await getDb();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    await store.delete(id);
    await tx.done;
  } else {
    const updated = getLSData().filter((item) => item.id !== id);
    saveLSData(updated);
  }
};

// Limpar tudo
export const clearStore = async () => {
  if (isIDBAvailable()) {
    const db = await getDb();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    await store.clear();
    await tx.done;
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
};

// Pré-carregar mídias
export const preloadMedias = async (mediaList: any[]) => {
  for (const media of mediaList) {
    if (media.type.startsWith("image")) {
      const img = new Image();
      img.src = URL.createObjectURL(media.blob);
    } else if (media.type.startsWith("video")) {
      const video = document.createElement("video");
      video.src = URL.createObjectURL(media.blob);
    }
  }
};
